import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  t8_subscribers_add_payment,
  t8_subscribers_create,
  t8_subscribers_get_by_serial,
  t8_subscribers_list_plans,
  t8_subscribers_summary,
  t8_subscribers_update,
} from '../../../utils/services';
import './SuscripcionesT8.css';

function formatPrice(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return value || '0.00';
  return `$${num.toFixed(2)}`;
}

function statusBadge(status) {
  if (status === 'activo') return <span className="badge bg-success">Activo</span>;
  if (status === 'mora') return <span className="badge bg-warning text-dark">En mora</span>;
  if (status === 'suspendido') return <span className="badge bg-danger">Suspendido</span>;
  return <span className="badge bg-secondary">{status || '—'}</span>;
}

function cycleLabel(cycle) {
  return cycle === 'annual' ? 'Anual' : 'Mensual';
}

function SuscripcionesT8({ permission, token }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionToken = token || localStorage.getItem('token');
  const canWrite = Number(permission) === 1;

  const [serialInput, setSerialInput] = useState('');
  const [summary, setSummary] = useState({ activo: 0, mora: 0, suspendido: 0, by_plan: [] });
  const [plans, setPlans] = useState([]);
  const [subscriber, setSubscriber] = useState(null);
  const [lookupSerial, setLookupSerial] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [errorMessage, setErrorMessage] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createSerial, setCreateSerial] = useState('');
  const [createPlanId, setCreatePlanId] = useState('');
  const [createCycle, setCreateCycle] = useState('monthly');
  const [createInvoice, setCreateInvoice] = useState('');
  const [createAmount, setCreateAmount] = useState('');
  const [createPaidAt, setCreatePaidAt] = useState('');
  const [savingCreate, setSavingCreate] = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payInvoice, setPayInvoice] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payPaidAt, setPayPaidAt] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);

  const [savingUpdate, setSavingUpdate] = useState(false);

  const showToast = useCallback((message, variant = 'success') => {
    setToastVariant(variant);
    setToastMessage(message);
  }, []);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const t = setTimeout(() => setToastMessage(''), 5000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  const refreshSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await t8_subscribers_summary(sessionToken);
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res && !res.error) {
        setSummary({
          activo: res.activo || 0,
          mora: res.mora || 0,
          suspendido: res.suspendido || 0,
          by_plan: Array.isArray(res.by_plan) ? res.by_plan : [],
        });
      }
    } finally {
      setSummaryLoading(false);
    }
  }, [sessionToken, navigate]);

  const loadPlans = useCallback(async () => {
    const res = await t8_subscribers_list_plans(sessionToken);
    if (res === 'INVALID') {
      navigate('/');
      return;
    }
    setPlans(Array.isArray(res) ? res : []);
  }, [sessionToken, navigate]);

  const lookupSerialFn = useCallback(async (serial, options = {}) => {
    const trimmed = String(serial || '').trim();
    if (!trimmed) return;

    setLoading(true);
    setErrorMessage('');
    setLookupSerial(trimmed);
    setSerialInput(trimmed);

    try {
      const res = await t8_subscribers_get_by_serial(sessionToken, trimmed);
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.code === 'NOT_FOUND' || (res?.error && res?.code === 'NOT_FOUND')) {
        setSubscriber(null);
        setErrorMessage('Serial no encontrado en el inventario.');
        return;
      }
      if (res?.error) {
        setSubscriber(null);
        setErrorMessage(res.message || 'Error al buscar el serial');
        return;
      }

      setSubscriber(res.subscriber || null);
      if (!res.subscriber) {
        setErrorMessage('');
        if (options.openCreateIfMissing && canWrite) {
          setCreateSerial(trimmed);
          setCreatePlanId(plans[0]?.id ? String(plans[0].id) : '');
          setCreateCycle('monthly');
          setCreateInvoice('');
          setCreateAmount('');
          setCreatePaidAt(new Date().toISOString().slice(0, 10));
          setShowCreateModal(true);
        }
      }
    } catch (err) {
      setErrorMessage('Error al buscar el serial');
      setSubscriber(null);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, navigate, canWrite]);

  useEffect(() => {
    if (!sessionToken) {
      navigate('/');
      return;
    }
    refreshSummary();
    loadPlans();
  }, [sessionToken, navigate, refreshSummary, loadPlans]);

  useEffect(() => {
    const serialFromUrl = searchParams.get('serial');
    if (!serialFromUrl) return;

    const openCreate = searchParams.get('create') === '1';
    lookupSerialFn(serialFromUrl, { openCreateIfMissing: openCreate });

    if (openCreate) {
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('serial'), searchParams.get('create')]);

  const handleSearch = () => {
    const trimmed = serialInput.trim();
    if (!trimmed) return;
    setSearchParams({ serial: trimmed });
    lookupSerialFn(trimmed);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const openCreateModal = (prefillSerial = '') => {
    setCreateSerial(prefillSerial || serialInput.trim() || '');
    setCreatePlanId(plans[0]?.id ? String(plans[0].id) : '');
    setCreateCycle('monthly');
    setCreateInvoice('');
    setCreateAmount('');
    setCreatePaidAt(new Date().toISOString().slice(0, 10));
    setShowCreateModal(true);
  };

  const handleCreate = async () => {
    if (savingCreate || !createSerial.trim() || !createPlanId) return;
    setSavingCreate(true);
    try {
      const payload = {
        serial: createSerial.trim(),
        plan_id: createPlanId,
        billing_cycle: createCycle,
      };
      if (createInvoice.trim()) {
        payload.invoice_number = createInvoice.trim();
        payload.amount = createAmount;
        payload.paid_at = createPaidAt || undefined;
      }
      const res = await t8_subscribers_create(sessionToken, payload);
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo crear la suscripción', 'danger');
        return;
      }
      showToast('Suscripción creada');
      setShowCreateModal(false);
      setSubscriber(res.subscriber);
      setLookupSerial(res.subscriber?.serial_num || createSerial.trim());
      setSearchParams({ serial: res.subscriber?.serial_num || createSerial.trim() });
      await refreshSummary();
    } catch (err) {
      showToast('Error al crear la suscripción', 'danger');
    } finally {
      setSavingCreate(false);
    }
  };

  const handleChangePlan = async (planId) => {
    if (!canWrite || !subscriber || savingUpdate) return;
    setSavingUpdate(true);
    try {
      const res = await t8_subscribers_update(sessionToken, {
        id: subscriber.id,
        plan_id: planId,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo actualizar el plan', 'danger');
        return;
      }
      setSubscriber(res.subscriber);
      showToast('Plan actualizado');
      await refreshSummary();
    } finally {
      setSavingUpdate(false);
    }
  };

  const handleChangeCycle = async (cycle) => {
    if (!canWrite || !subscriber || savingUpdate) return;
    setSavingUpdate(true);
    try {
      const res = await t8_subscribers_update(sessionToken, {
        id: subscriber.id,
        billing_cycle: cycle,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo actualizar el ciclo', 'danger');
        return;
      }
      setSubscriber(res.subscriber);
      showToast('Ciclo actualizado');
    } finally {
      setSavingUpdate(false);
    }
  };

  const handleToggleSuspended = async () => {
    if (!canWrite || !subscriber || savingUpdate) return;
    const next = subscriber.suspended ? 0 : 1;
    setSavingUpdate(true);
    try {
      const res = await t8_subscribers_update(sessionToken, {
        id: subscriber.id,
        suspended: next,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo actualizar el estado', 'danger');
        return;
      }
      setSubscriber(res.subscriber);
      showToast(next ? 'Cuenta suspendida' : 'Suspensión removida');
      await refreshSummary();
    } finally {
      setSavingUpdate(false);
    }
  };

  const openPaymentModal = () => {
    setPayInvoice('');
    setPayAmount('');
    setPayPaidAt(new Date().toISOString().slice(0, 10));
    setPayNotes('');
    setShowPaymentModal(true);
  };

  const handleAddPayment = async () => {
    if (savingPayment || !subscriber || !payInvoice.trim() || payAmount === '') return;
    setSavingPayment(true);
    try {
      const res = await t8_subscribers_add_payment(sessionToken, {
        subscriber_id: subscriber.id,
        invoice_number: payInvoice.trim(),
        amount: payAmount,
        paid_at: payPaidAt || undefined,
        notes: payNotes.trim() || undefined,
      });
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res?.error) {
        showToast(res.message || 'No se pudo registrar el pago', 'danger');
        return;
      }
      setSubscriber(res.subscriber);
      setShowPaymentModal(false);
      showToast('Pago registrado');
      await refreshSummary();
    } catch (err) {
      showToast('Error al registrar el pago', 'danger');
    } finally {
      setSavingPayment(false);
    }
  };

  const clearLookup = () => {
    setSubscriber(null);
    setLookupSerial(null);
    setErrorMessage('');
    setSerialInput('');
    setSearchParams({});
  };

  const showLanding = !lookupSerial;

  return (
    <div className="suscripciones-t8-page">
      {toastMessage && (
        <div className={`alert alert-${toastVariant} suscripciones-t8-toast`} role="alert">
          {toastMessage}
        </div>
      )}

      {showLanding ? (
        <div className="d-flex justify-content-center align-items-start pt-4" style={{ minHeight: '80vh' }}>
          <div className="text-center" style={{ width: '100%', maxWidth: '720px' }}>
            <h1 className="suscripciones-t8-title">Suscripciones de T8</h1>
            <p className="suscripciones-t8-subtitle">Busca por serial del dispositivo</p>

            <div className="mt-4 px-3">
              <input
                type="text"
                className="form-control form-control-lg suscripciones-t8-search"
                placeholder="Escribe el serial..."
                value={serialInput}
                onChange={(e) => setSerialInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <div className="d-flex justify-content-center gap-2 mt-4 flex-wrap">
                <button
                  className="btn btn-lg px-5 text-white suscripciones-t8-btn"
                  type="button"
                  onClick={handleSearch}
                  disabled={!serialInput.trim() || loading}
                >
                  Buscar
                </button>
                {canWrite && (
                  <button
                    className="btn btn-lg btn-outline-primary px-4"
                    type="button"
                    onClick={() => openCreateModal()}
                  >
                    Nueva suscripción
                  </button>
                )}
              </div>
            </div>

            <div className="suscripciones-t8-summary mt-5">
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="suscripciones-t8-card suscripciones-t8-card-activo">
                    <div className="suscripciones-t8-card-value">{summaryLoading ? '…' : summary.activo}</div>
                    <div className="suscripciones-t8-card-label">Activos</div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="suscripciones-t8-card suscripciones-t8-card-mora">
                    <div className="suscripciones-t8-card-value">{summaryLoading ? '…' : summary.mora}</div>
                    <div className="suscripciones-t8-card-label">En mora</div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="suscripciones-t8-card suscripciones-t8-card-suspendido">
                    <div className="suscripciones-t8-card-value">{summaryLoading ? '…' : summary.suspendido}</div>
                    <div className="suscripciones-t8-card-label">Suspendidos</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-start">
                <h5 className="mb-3">Suscritos por plan</h5>
                {summary.by_plan.length === 0 ? (
                  <p className="text-muted mb-0">No hay planes registrados.</p>
                ) : (
                  <Table bordered hover size="sm" className="bg-white">
                    <thead className="table-light">
                      <tr>
                        <th>Plan</th>
                        <th style={{ width: '120px' }} className="text-center">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.by_plan.map((row) => (
                        <tr key={row.plan_id}>
                          <td>{row.plan_name}</td>
                          <td className="text-center">{row.subscriber_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="container-fluid py-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <button type="button" className="btn btn-link ps-0" onClick={clearLookup}>
                ← Volver al resumen
              </button>
              <h2 className="h4 mb-0">Serial: <span className="font-monospace">{lookupSerial}</span></h2>
            </div>
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: '220px' }}
                placeholder="Otro serial..."
                value={serialInput}
                onChange={(e) => setSerialInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button className="btn btn-primary" type="button" onClick={handleSearch} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>

          {loading && <p className="text-muted">Buscando…</p>}

          {!loading && errorMessage && (
            <div className="alert alert-warning">
              {errorMessage}
            </div>
          )}

          {!loading && !subscriber && !errorMessage && (
            <div className="alert alert-info">
              Este dispositivo no tiene suscripción registrada.
              {canWrite && (
                <div className="mt-2">
                  <Button size="sm" variant="primary" onClick={() => openCreateModal(lookupSerial)}>
                    Crear suscripción
                  </Button>
                </div>
              )}
            </div>
          )}

          {!loading && subscriber && (
            <div className="row g-3">
              <div className="col-lg-5">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h5 className="card-title mb-0">Detalle</h5>
                      {statusBadge(subscriber.status)}
                    </div>

                    <dl className="row mb-0 suscripciones-t8-dl">
                      <dt className="col-sm-5">Serial</dt>
                      <dd className="col-sm-7 font-monospace">{subscriber.serial_num}</dd>

                      <dt className="col-sm-5">Plan</dt>
                      <dd className="col-sm-7">
                        {canWrite ? (
                          <Form.Select
                            size="sm"
                            value={String(subscriber.plan_id)}
                            disabled={savingUpdate}
                            onChange={(e) => handleChangePlan(e.target.value)}
                          >
                            {plans.map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </Form.Select>
                        ) : (
                          subscriber.plan_name || '—'
                        )}
                      </dd>

                      <dt className="col-sm-5">Ciclo</dt>
                      <dd className="col-sm-7">
                        {canWrite ? (
                          <Form.Select
                            size="sm"
                            value={subscriber.billing_cycle}
                            disabled={savingUpdate}
                            onChange={(e) => handleChangeCycle(e.target.value)}
                          >
                            <option value="monthly">Mensual</option>
                            <option value="annual">Anual</option>
                          </Form.Select>
                        ) : (
                          cycleLabel(subscriber.billing_cycle)
                        )}
                      </dd>

                      <dt className="col-sm-5">Próximo vencimiento</dt>
                      <dd className="col-sm-7">{subscriber.next_due_date || '—'}</dd>

                      <dt className="col-sm-5">Precio ref.</dt>
                      <dd className="col-sm-7">
                        {subscriber.billing_cycle === 'annual'
                          ? formatPrice(subscriber.price_annual)
                          : formatPrice(subscriber.price_monthly)}
                      </dd>
                    </dl>

                    {canWrite && (
                      <div className="d-flex gap-2 mt-3 flex-wrap">
                        <Button
                          size="sm"
                          variant={subscriber.suspended ? 'outline-success' : 'outline-danger'}
                          disabled={savingUpdate}
                          onClick={handleToggleSuspended}
                        >
                          {subscriber.suspended ? 'Reactivar' : 'Suspender'}
                        </Button>
                        <Button size="sm" variant="primary" onClick={openPaymentModal}>
                          Registrar pago
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-7">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <h5 className="card-title">Pagos / Facturas</h5>
                    {(subscriber.payments || []).length === 0 ? (
                      <p className="text-muted mb-0">Sin pagos registrados.</p>
                    ) : (
                      <Table bordered hover size="sm" responsive className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Factura</th>
                            <th>Monto</th>
                            <th>Fecha</th>
                            <th>Notas</th>
                            <th>Registró</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subscriber.payments.map((pay) => (
                            <tr key={pay.id}>
                              <td>{pay.invoice_number}</td>
                              <td>{formatPrice(pay.amount)}</td>
                              <td>{pay.paid_at}</td>
                              <td>{pay.notes || '—'}</td>
                              <td>{pay.created_by_name || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nueva suscripción</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Serial</Form.Label>
            <Form.Control
              value={createSerial}
              onChange={(e) => setCreateSerial(e.target.value)}
              placeholder="Serial del dispositivo"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Plan</Form.Label>
            <Form.Select value={createPlanId} onChange={(e) => setCreatePlanId(e.target.value)}>
              <option value="">Selecciona un plan…</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (mensual {formatPrice(p.price_monthly)} / anual {formatPrice(p.price_annual)})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Ciclo de cobro</Form.Label>
            <Form.Select value={createCycle} onChange={(e) => setCreateCycle(e.target.value)}>
              <option value="monthly">Mensual</option>
              <option value="annual">Anual</option>
            </Form.Select>
          </Form.Group>
          <hr />
          <p className="text-muted small">Pago inicial (opcional). Sin factura la suscripción queda en mora hasta el primer pago.</p>
          <Form.Group className="mb-3">
            <Form.Label>Nº factura</Form.Label>
            <Form.Control value={createInvoice} onChange={(e) => setCreateInvoice(e.target.value)} />
          </Form.Group>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Monto</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={createAmount}
                  onChange={(e) => setCreateAmount(e.target.value)}
                  disabled={!createInvoice.trim()}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Fecha de pago</Form.Label>
                <Form.Control
                  type="date"
                  value={createPaidAt}
                  onChange={(e) => setCreatePaidAt(e.target.value)}
                  disabled={!createInvoice.trim()}
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={savingCreate || !createSerial.trim() || !createPlanId}
            onClick={handleCreate}
          >
            {savingCreate ? 'Guardando…' : 'Crear'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Registrar pago</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nº factura</Form.Label>
            <Form.Control value={payInvoice} onChange={(e) => setPayInvoice(e.target.value)} />
          </Form.Group>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Monto</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Fecha de pago</Form.Label>
                <Form.Control
                  type="date"
                  value={payPaidAt}
                  onChange={(e) => setPayPaidAt(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-0">
            <Form.Label>Notas</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={payNotes}
              onChange={(e) => setPayNotes(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={savingPayment || !payInvoice.trim() || payAmount === ''}
            onClick={handleAddPayment}
          >
            {savingPayment ? 'Guardando…' : 'Registrar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SuscripcionesT8;
